import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { toastify } from '../../../../../../utils/toast';

const AssignedCustomersTab = () => {
    const { meterId } = useParams();
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [availableCustomers, setAvailableCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showUnassignDialog, setShowUnassignDialog] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [meterData, setMeterData] = useState(null);
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    // Form data for assigning new customer
    const [formData, setFormData] = useState({
        customerType: 'existing',
        customerId: '',
        customerName: '',
        phoneNumber: '',
        email: '',
        paymentType: '',
        accountBalance: 0
    });

    // Form data for editing customer
    const [editFormData, setEditFormData] = useState({
        customerName: '',
        phoneNumber: '',
        email: ''
    });

    const paymentTypeOptions = [
        { label: 'Prepaid', value: 'Prepaid' },
        { label: 'Postpaid', value: 'Postpaid' },
        { label: 'Token Based', value: 'Token Based' }
    ];

    const customerTypeOptions = [
        { label: 'Select Existing Customer', value: 'existing' },
        { label: 'Create New Customer', value: 'new' }
    ];

    useEffect(() => {
        if (meterId && selectedFacilityId) {
            fetchMeterData();
            fetchAssignedCustomers();
        }
    }, [meterId, selectedFacilityId]);

    // Fetch meter data to get deviceId and other required fields
    const fetchMeterData = async () => {
        try {
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/power_meters/get_meter/${meterId}`,
                'GET'
            );

            if (response && response.success) {
                setMeterData(response.data.data || response.data);
            } else {
                toastify('Failed to fetch meter details', 'error');
            }
        } catch (error) {
            console.error('Error fetching meter data:', error);
            toastify('Error loading meter details', 'error');
        }
    };

    // Fetch customers assigned to this meter
    const fetchAssignedCustomers = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/customer_management/meter_customer_accounts/${meterId}`,
                'GET'
            );

            if (response && response.success) {
                const customersData = response.data.data || [];
                const customersArray = Array.isArray(customersData) ? customersData : [];
                
                // Fetch balances for all customers
                if (customersArray.length > 0) {
                    await fetchCustomersBalances(customersArray);
                } else {
                    setCustomers([]);
                }
            } else {
                setCustomers([]);
            }
        } catch (error) {
            console.error('Error fetching assigned customers:', error);
            setCustomers([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch balances for all customers at once
    const fetchCustomersBalances = async (customersData) => {
        try {
            setBalanceLoading(true);

            // Create an array of balance fetch promises
            const balancePromises = customersData.map(customer =>
                makeRequest2(
                    `/api/app/power_meter_management/controllers/customer_management/get_customer_account_balance/${customer._id}`,
                    'GET'
                ).catch(error => {
                    console.error(`Error fetching balance for customer ${customer.account_no}:`, error);
                    return null;
                })
            );

            // Wait for all balance requests to complete
            const balanceResponses = await Promise.allSettled(balancePromises);

            // Update customers with their balance data
            const updatedCustomers = customersData.map((customer, index) => {
                const balanceResult = balanceResponses[index];

                if (balanceResult.status === 'fulfilled' &&
                    balanceResult.value &&
                    balanceResult.value.success) {

                    const balanceData = balanceResult.value.data.data;
                    return {
                        ...customer,
                        currentBalance: balanceData.currentBalance || 0,
                        negativeBalance: balanceData.negativeBalance || 0,
                        balanceLoaded: true
                    };
                } else {
                    return {
                        ...customer,
                        currentBalance: customer.accountBalance || 0,
                        negativeBalance: 0,
                        balanceLoaded: false
                    };
                }
            });

            setCustomers(updatedCustomers);
        } catch (error) {
            console.error('Error fetching customers balances:', error);
            setCustomers(customersData);
        } finally {
            setBalanceLoading(false);
        }
    };

    // Fetch available customers for assignment
    const fetchAvailableCustomers = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `/api/app/customer_management/get_customers/${selectedFacilityId}`,
                'GET'
            );
            if (response.success) {
                const customersData = response.data?.customers || response.data || [];
                setAvailableCustomers(customersData);
            }
        } catch (error) {
            console.error('Error fetching available customers:', error);
            toastify('Error loading available customers', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (field === 'customerType') {
            setFormData(prev => ({
                ...prev,
                customerId: '',
                customerName: '',
                phoneNumber: '',
                email: ''
            }));
        }
    };

    const handleEditInputChange = (field, value) => {
        setEditFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAssignCustomer = async () => {
        if (formData.customerType === 'existing' && !formData.customerId) {
            toastify('Please select a customer', 'error');
            return;
        }

        if (formData.customerType === 'new') {
            if (!formData.customerName || !formData.phoneNumber) {
                toastify('Please fill in customer name and phone number', 'error');
                return;
            }
        }

        if (!formData.paymentType) {
            toastify('Please select payment type', 'error');
            return;
        }

        if (!meterData) {
            toastify('Meter data not available. Please try again.', 'error');
            return;
        }

        setLoading(true);
        try {
            let customerData = {};

            if (formData.customerType === 'existing') {
                const existingCustomer = availableCustomers.find(c => c._id === formData.customerId);
                if (!existingCustomer) {
                    toastify('Selected customer not found', 'error');
                    return;
                }
                customerData = {
                    customerId: existingCustomer._id,
                    customerName: existingCustomer.fullName || `${existingCustomer.firstName} ${existingCustomer.lastName}`,
                    phoneNumber: existingCustomer.phoneNumber,
                    email: existingCustomer.email || ''
                };
            } else {
                customerData = {
                    customerName: formData.customerName,
                    phoneNumber: formData.phoneNumber,
                    email: formData.email || ''
                };
            }

            const accountData = {
                facilityId: selectedFacilityId,
                account_no: meterData.accountNumber ? meterData.accountNumber.toString() : `ACC-${meterData.meterSerialNumber}-${Date.now()}`,
                customerName: customerData.customerName,
                phoneNumber: customerData.phoneNumber,
                deviceId: meterData.deviceId ? meterData.deviceId.toString() : meterData.meterSerialNumber,
                payment_type: formData.paymentType,
                currentReading: meterData.currentMeterReading || 0,
                customerId: customerData.customerId || undefined,
                email: customerData.email || '',
                unitId: meterData.unitId || undefined,
                meterId: meterId,
                previousReading: meterData.previousMeterReading || 0,
                accountBalance: Number(formData.accountBalance) || 0,
                status: 'Active'
            };

            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/customer_management/add_customer_account`,
                'POST',
                accountData
            );

            if (response.success) {
                toastify('Customer assigned successfully', 'success');
                handleCloseModal();
                fetchAssignedCustomers();
            } else {
                console.error('Assignment failed:', response);
                toastify(response.error || response.message || 'Failed to assign customer', 'error');
            }
        } catch (error) {
            console.error('Error assigning customer:', error);
            toastify('Error assigning customer: ' + (error.message || 'Unknown error'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEditCustomer = async () => {
        if (!selectedCustomer || !editFormData.customerName || !editFormData.phoneNumber) {
            toastify('Please fill in required fields (Name and Phone)', 'error');
            return;
        }

        setLoading(true);
        try {
            const updateData = {
                customerName: editFormData.customerName.trim(),
                phoneNumber: editFormData.phoneNumber.trim(),
                email: editFormData.email ? editFormData.email.trim() : ''
            };

            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/customer_management/edit_customer_account/${selectedCustomer._id}`,
                'PUT',
                updateData
            );

            if (response.success) {
                toastify('Customer updated successfully', 'success');
                handleCloseEditModal();
                fetchAssignedCustomers();
            } else {
                console.error('Update failed:', response);
                toastify(response.error || response.message || 'Failed to update customer', 'error');
            }
        } catch (error) {
            console.error('Error updating customer:', error);
            toastify('Error updating customer: ' + (error.message || 'Unknown error'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUnassignCustomer = async () => {
        if (!selectedCustomer) return;

        setLoading(true);
        try {
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/customer_management/deactivate_customer_account/${selectedCustomer._id}`,
                'PUT'
            );

            if (response.success) {
                toastify('Customer unassigned successfully', 'success');
                setShowUnassignDialog(false);
                setSelectedCustomer(null);
                fetchAssignedCustomers();
            } else {
                toastify(response.error || 'Failed to unassign customer', 'error');
            }
        } catch (error) {
            console.error('Error unassigning customer:', error);
            toastify('Error unassigning customer', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshBalances = async () => {
        if (customers.length > 0) {
            await fetchCustomersBalances(customers);
            toastify('Balances refreshed successfully', 'success');
        }
    };

    const handleCloseModal = () => {
        setShowAssignModal(false);
        setFormData({
            customerType: 'existing',
            customerId: '',
            customerName: '',
            phoneNumber: '',
            email: '',
            paymentType: '',
            accountBalance: 0
        });
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedCustomer(null);
        setEditFormData({
            customerName: '',
            phoneNumber: '',
            email: ''
        });
    };

    const prepareEditCustomer = (customer) => {
        setSelectedCustomer(customer);
        setEditFormData({
            customerName: customer.customerName || '',
            phoneNumber: customer.phoneNumber || '',
            email: customer.email || ''
        });
        setShowEditModal(true);
    };

    const handleViewCustomer = (customer) => {
        const { payment_type, _id } = customer;

        const getNavigationPath = (paymentType, accountId) => {
            const basePath = "/facility/utility_management/meter_management/power_meters/view_power_meter_tabs";

            switch (paymentType) {
                case 'Prepaid':
                    return `${basePath}/view_prepaid_power_customer/${accountId}`;
                case 'Postpaid':
                    return `${basePath}/view_postpaid_power_customer/${accountId}`;
                case 'Token Based':
                    return `${basePath}/view_prepaid_power_customer/${accountId}`;
                default:
                    console.warn(`Unknown payment type: ${paymentType}. Defaulting to prepaid view.`);
                    return `${basePath}/view_prepaid_power_customer/${accountId}`;
            }
        };

        const navigationPath = getNavigationPath(payment_type, _id);
        navigate(navigationPath);
    };

    const prepareUnassignCustomer = (customer) => {
        setSelectedCustomer(customer);
        setShowUnassignDialog(true);
    };

    // Template functions for DataTable
    const statusBodyTemplate = (rowData) => {
        return (
            <span className={`badge ${rowData.status === 'Active' ? 'bg-success' : 'bg-danger'}`}>
                {rowData.status}
            </span>
        );
    };

    const paymentTypeBodyTemplate = (rowData) => {
        const getPaymentTypeBadgeClass = (type) => {
            switch (type) {
                case 'Prepaid': return 'bg-success';
                case 'Postpaid': return 'bg-primary';
                case 'Token Based': return 'bg-warning';
                default: return 'bg-secondary';
            }
        };

        return (
            <span className={`badge ${getPaymentTypeBadgeClass(rowData.payment_type)}`}>
                {rowData.payment_type}
            </span>
        );
    };

    const balanceBodyTemplate = (rowData) => {
        if (balanceLoading && !rowData.balanceLoaded) {
            return (
                <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-1" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <small>Loading...</small>
                </div>
            );
        }

        const balance = rowData.currentBalance || 0;
        const isNegative = balance < 0;

        return (
            <span className={isNegative ? 'text-danger fw-bold' : 'text-success fw-bold'}>
                KES {balance.toFixed(2)}
            </span>
        );
    };

    const negativeBalanceBodyTemplate = (rowData) => {
        if (balanceLoading && !rowData.balanceLoaded) {
            return (
                <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-1" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <small>Loading...</small>
                </div>
            );
        }

        const negativeBalance = rowData.negativeBalance || 0;

        return (
            <span className={negativeBalance > 0 ? 'text-danger fw-bold' : 'text-muted'}>
                KES {negativeBalance.toFixed(2)}
            </span>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="d-flex gap-1">
                <Button
                    icon="pi pi-eye"
                    size="small"
                    outlined
                    onClick={() => handleViewCustomer(rowData)}
                    tooltip="View Customer"
                />
                <Button
                    icon="pi pi-pencil"
                    size="small"
                    outlined
                    severity="success"
                    onClick={() => prepareEditCustomer(rowData)}
                    tooltip="Edit Customer"
                />
                <Button
                    icon="pi pi-user-minus"
                    size="small"
                    outlined
                    severity="danger"
                    onClick={() => prepareUnassignCustomer(rowData)}
                    tooltip="Unassign Customer"
                />
            </div>
        );
    };

    const renderAssignModal = () => (
        <Dialog
            header="Assign Customer to Meter"
            visible={showAssignModal}
            onHide={handleCloseModal}
            style={{ width: '50vw' }}
            modal
        >
            <div className="p-fluid">
                {!meterData && (
                    <div className="alert alert-warning mb-3">
                        <i className="pi pi-exclamation-triangle"></i> Loading meter data...
                    </div>
                )}

                <div className="field mb-3">
                    <label className="form-label">Customer Type</label>
                    <Dropdown
                        value={formData.customerType}
                        options={customerTypeOptions}
                        onChange={(e) => handleInputChange('customerType', e.value)}
                        placeholder="Select customer type"
                    />
                </div>

                {formData.customerType === 'existing' && (
                    <div className="field mb-3">
                        <label className="form-label">Select Customer</label>
                        <Dropdown
                            value={formData.customerId}
                            options={availableCustomers.map(customer => ({
                                label: customer.fullName || `${customer.firstName} ${customer.lastName}`,
                                value: customer._id
                            }))}
                            onChange={(e) => handleInputChange('customerId', e.value)}
                            placeholder="Select customer"
                            filter
                        />
                    </div>
                )}

                {formData.customerType === 'new' && (
                    <>
                        <div className="field mb-3">
                            <label className="form-label">Customer Name *</label>
                            <InputText
                                value={formData.customerName}
                                onChange={(e) => handleInputChange('customerName', e.target.value)}
                                placeholder="Enter customer name"
                                required
                            />
                        </div>
                        <div className="field mb-3">
                            <label className="form-label">Phone Number *</label>
                            <InputText
                                value={formData.phoneNumber}
                                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                                placeholder="Enter phone number"
                                required
                            />
                        </div>
                        <div className="field mb-3">
                            <label className="form-label">Email (Optional)</label>
                            <InputText
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                placeholder="Enter email address"
                            />
                        </div>
                    </>
                )}

                <div className="field mb-3">
                    <label className="form-label">Payment Type *</label>
                    <Dropdown
                        value={formData.paymentType}
                        options={paymentTypeOptions}
                        onChange={(e) => handleInputChange('paymentType', e.value)}
                        placeholder="Select payment type"
                    />
                </div>

                {formData.paymentType === 'Prepaid' && (
                    <div className="field mb-3">
                        <label className="form-label">Initial Account Balance (KES)</label>
                        <InputText
                            type="number"
                            step="0.01"
                            value={formData.accountBalance}
                            onChange={(e) => handleInputChange('accountBalance', parseFloat(e.target.value) || 0)}
                            placeholder="0.00"
                        />
                    </div>
                )}

                {meterData && (
                    <div className="field mb-3">
                        <label className="form-label">Meter Information</label>
                        <div className="p-3 border rounded bg-light">
                            <small>
                                <strong>Serial Number:</strong> {meterData.meterSerialNumber}<br />
                                <strong>Account Number:</strong> {meterData.accountNumber || 'Not Set'}<br />
                                <strong>Device ID:</strong> {meterData.deviceId || 'Not Set'}<br />
                                <strong>Gateway ID:</strong> {meterData.gatewayId || 'Not Set'}<br />
                                <strong>Current Reading:</strong> {meterData.currentMeterReading || 0} kWh<br />
                                <strong>Unit:</strong> {meterData.unitName || 'Not Assigned'}
                            </small>
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button
                        label="Cancel"
                        outlined
                        onClick={handleCloseModal}
                        disabled={loading}
                    />
                    <Button
                        label={loading ? 'Assigning...' : 'Assign Customer'}
                        onClick={handleAssignCustomer}
                        disabled={loading || !meterData}
                    />
                </div>
            </div>
        </Dialog>
    );

    const renderEditModal = () => (
        <Dialog
            header="Edit Customer Details"
            visible={showEditModal}
            onHide={handleCloseEditModal}
            style={{ width: '40vw' }}
            modal
        >
            <div className="p-fluid">
                {selectedCustomer && (
                    <div className="field mb-3">
                        <label className="form-label">Current Customer Information</label>
                        <div className="p-3 border rounded bg-light">
                            <small>
                                <strong>Account No:</strong> {selectedCustomer.account_no}<br />
                                <strong>Payment Type:</strong> {selectedCustomer.payment_type}<br />
                                <strong>Status:</strong> {selectedCustomer.status}
                            </small>
                        </div>
                    </div>
                )}

                <div className="field mb-3">
                    <label className="form-label">Customer Name *</label>
                    <InputText
                        value={editFormData.customerName}
                        onChange={(e) => handleEditInputChange('customerName', e.target.value)}
                        placeholder="Enter customer name"
                        required
                    />
                </div>

                <div className="field mb-3">
                    <label className="form-label">Phone Number *</label>
                    <InputText
                        value={editFormData.phoneNumber}
                        onChange={(e) => handleEditInputChange('phoneNumber', e.target.value)}
                        placeholder="Enter phone number"
                        required
                    />
                </div>

                <div className="field mb-3">
                    <label className="form-label">Email</label>
                    <InputText
                        value={editFormData.email}
                        onChange={(e) => handleEditInputChange('email', e.target.value)}
                        placeholder="Enter email address"
                    />
                </div>

                <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button
                        label="Cancel"
                        outlined
                        onClick={handleCloseEditModal}
                        disabled={loading}
                    />
                    <Button
                        label={loading ? 'Updating...' : 'Update Customer'}
                        onClick={handleEditCustomer}
                        disabled={loading}
                    />
                </div>
            </div>
        </Dialog>
    );

    const renderUnassignDialog = () => (
        <Dialog
            header="Confirm Unassign"
            visible={showUnassignDialog}
            onHide={() => setShowUnassignDialog(false)}
            style={{ width: '30vw' }}
            modal
        >
            <div>
                <p>Are you sure you want to unassign this customer from the meter?</p>
                <p><strong>Customer:</strong> {selectedCustomer?.customerName}</p>
                <p><strong>Account:</strong> {selectedCustomer?.account_no}</p>

                <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button
                        label="Cancel"
                        outlined
                        onClick={() => setShowUnassignDialog(false)}
                        disabled={loading}
                    />
                    <Button
                        label={loading ? 'Processing...' : 'Confirm'}
                        severity="danger"
                        onClick={handleUnassignCustomer}
                        disabled={loading}
                    />
                </div>
            </div>
        </Dialog>
    );

    return (
        <div className="tab-pane show active" role="tabpanel">
            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <Button
                                label="Assign Customer"
                                className="btn btn-primary btn-sm"
                                onClick={() => {
                                    setShowAssignModal(true);
                                    fetchAvailableCustomers();
                                }}
                                disabled={!meterData}
                            />
                            <Button
                                label="Refresh Balances"
                                icon="pi pi-refresh"
                                className="btn btn-primary btn-sm"
                                onClick={handleRefreshBalances}
                                loading={balanceLoading}
                                disabled={customers.length === 0}
                            />
                        </div>
                        <div className="card-body">
                            <div className="dt-responsive table-responsive">
                                <DataTable
                                    value={customers}
                                    loading={loading}
                                    emptyMessage="No customers assigned to this meter."
                                    sortMode="multiple"
                                    paginator
                                    rows={10}
                                    stripedRows
                                    tableStyle={{ minWidth: '50rem' }}
                                >
                                    <Column field="customerName" sortable header="Name" />
                                    <Column field="phoneNumber" sortable header="Phone" />
                                    <Column field="account_no" sortable header="Account Number" />
                                    <Column field="payment_type" sortable header="Payment Type" body={paymentTypeBodyTemplate} />
                                    <Column field="currentBalance" sortable header="Current Balance" body={balanceBodyTemplate} />
                                    <Column field="negativeBalance" sortable header="Negative Balance" body={negativeBalanceBodyTemplate} />
                                    <Column field="status" sortable header="Status" body={statusBodyTemplate} />
                                    <Column header="Actions" body={actionBodyTemplate} />
                                </DataTable>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {renderAssignModal()}
            {renderEditModal()}
            {renderUnassignDialog()}
        </div>
    );
};

export default AssignedCustomersTab;